import type {
  DsarCase,
  DsarCaseAsset,
  DsarCaseAssetDTO,
  DsarCaseAssetOwnerSummary,
  DsarCaseAssetOwnerSummaryDTO,
  DsarCaseAssetRequestTypeResult,
  DsarCaseAssetRequestTypeResultDTO,
  DsarCaseAssetSummary,
  DsarCaseAssetSummaryDTO,
  DsarCaseAssetStatusLog,
  DsarCaseAssetStatusLogDTO,
  DsarCaseCommunicationLog,
  DsarCaseCommunicationLogDTO,
  DsarCaseDTO,
  DsarEligibleAsset,
  DsarEligibleAssetDTO,
  DsarCaseRequestType,
  DsarCaseRequestTypeDTO,
  DsarCaseAssetCaseSummary,
  DsarCaseAssetCaseSummaryDTO,
  DsarDomainValueSummary,
  DsarDomainValueSummaryDTO,
} from './dsar.types';

export const parseDsarDomainValueForFE = (dto: DsarDomainValueSummaryDTO): DsarDomainValueSummary => ({
  id: dto.id,
  group: dto.group,
  code: dto.code,
  name: dto.name,
  nameTranslationKey: dto.name_translation_key,
  displayOrder: dto.display_order,
});

const emptyDomainValue = (group: string): DsarDomainValueSummary => ({
  id: 0,
  group,
  code: '',
  name: '-',
  nameTranslationKey: null,
  displayOrder: 0,
});

export const parseDsarCaseRequestTypeForFE = (dto: DsarCaseRequestTypeDTO): DsarCaseRequestType => ({
  id: dto.id,
  requestType: parseDsarDomainValueForFE(dto.request_type),
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseDsarCaseCommunicationLogForFE = (dto: DsarCaseCommunicationLogDTO): DsarCaseCommunicationLog => ({
  id: dto.id,
  communicationType: dto.communication_type,
  sentAt: dto.sent_at,
  sentByUser: dto.sent_by_user,
  sentByUserName: dto.sent_by_user_name,
  daysSinceReceived: dto.days_since_received,
  comment: dto.comment,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseDsarCaseForFE = (dto: DsarCaseDTO): DsarCase => ({
  id: dto.id,
  caseReference: dto.case_reference,
  requesterName: dto.requester_name,
  requesterEmail: dto.requester_email,
  requesterPhone: dto.requester_phone,
  requesterCustomerNumber: dto.requester_customernumber,
  requesterSsn: dto.requester_ssn,
  requesterAddressLine1: dto.requester_address_line1,
  requesterAddressLine2: dto.requester_address_line2,
  requesterPostalCode: dto.requester_postalcode,
  requesterCity: dto.requester_city,
  requesterCountry: dto.requester_country,
  identityVerificationStatus: parseDsarDomainValueForFE(dto.identity_verification_status),
  identityVerificationNotes: dto.identity_verification_notes,
  status: parseDsarDomainValueForFE(dto.status),
  receivedAt: dto.received_at,
  dueDate: dto.due_date,
  closedAt: dto.closed_at,
  closedByUser: dto.closed_by_user,
  closedByUserName: dto.closed_by_user_name,
  retentionUntil: dto.retention_until,
  adminInstructionComment: dto.admin_instruction_comment,
  instructionLink: dto.instruction_link,
  portabilityInstruction: dto.portability_instruction,
  portabilityOutputLink: dto.portability_output_link,
  responseSummary: dto.response_summary,
  responseSentAt: dto.response_sent_at,
  responseSentByUser: dto.response_sent_by_user,
  distributedAt: dto.distributed_at,
  distributedByUser: dto.distributed_by_user,
  identifiersAnonymizedAt: dto.identifiers_anonymized_at,
  communicationLogs: (dto.communication_logs || []).map(parseDsarCaseCommunicationLogForFE),
  requestTypes: dto.request_types.map(parseDsarCaseRequestTypeForFE),
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseDsarCasesForFE = (dtos: DsarCaseDTO[]): DsarCase[] => dtos.map(parseDsarCaseForFE);

export const parseDsarCaseAssetOwnerForFE = (dto: DsarCaseAssetOwnerSummaryDTO): DsarCaseAssetOwnerSummary => ({
  id: dto.id,
  email: dto.email,
  firstName: dto.first_name,
  lastName: dto.last_name,
  fullName: [dto.first_name, dto.last_name].filter(Boolean).join(' ').trim() || dto.email,
});

export const parseDsarCaseAssetCaseSummaryForFE = (dto: DsarCaseAssetCaseSummaryDTO): DsarCaseAssetCaseSummary => ({
  id: dto.id,
  caseReference: dto.case_reference,
  requesterName: dto.requester_name,
  requesterEmail: dto.requester_email,
  requesterPhone: dto.requester_phone,
  requesterCustomerNumber: dto.requester_customernumber,
  requesterSsn: dto.requester_ssn,
  requesterAddressLine1: dto.requester_address_line1,
  requesterAddressLine2: dto.requester_address_line2,
  requesterPostalCode: dto.requester_postalcode,
  requesterCity: dto.requester_city,
  requesterCountry: dto.requester_country,
  receivedAt: dto.received_at,
  dueDate: dto.due_date,
  closedAt: dto.closed_at,
  status: dto.status ? parseDsarDomainValueForFE(dto.status) : emptyDomainValue('case_status'),
  identityVerificationStatus: dto.identity_verification_status
    ? parseDsarDomainValueForFE(dto.identity_verification_status)
    : emptyDomainValue('identity_verification_status'),
  adminInstructionComment: dto.admin_instruction_comment,
  instructionLink: dto.instruction_link,
  portabilityInstruction: dto.portability_instruction,
  portabilityOutputLink: dto.portability_output_link,
});

export const parseDsarCaseAssetSummaryForFE = (dto: DsarCaseAssetSummaryDTO): DsarCaseAssetSummary => ({
  id: dto.id,
  key: dto.key,
  name: dto.name,
  owner: parseDsarCaseAssetOwnerForFE(dto.owner),
});

export const parseDsarEligibleAssetForFE = (dto: DsarEligibleAssetDTO): DsarEligibleAsset => ({
  id: dto.id,
  name: dto.name,
  owner: parseDsarCaseAssetOwnerForFE(dto.owner),
  assetType: dto.asset_type,
  identifiableInformation: dto.identifiable_information,
  sensitiveIndividualInformation: dto.sensitive_individual_information,
});

export const parseDsarEligibleAssetsForFE = (dtos: DsarEligibleAssetDTO[]): DsarEligibleAsset[] => dtos.map(parseDsarEligibleAssetForFE);

export const parseDsarCaseAssetRequestTypeResultForFE = (dto: DsarCaseAssetRequestTypeResultDTO): DsarCaseAssetRequestTypeResult => ({
  id: dto.id,
  requestType: parseDsarDomainValueForFE(dto.request_type),
  handlingStatus: parseDsarDomainValueForFE(dto.handling_status),
  resultSummary: dto.result_summary,
  blockingReason: dto.blocking_reason,
  earliestPossibleActionDate: dto.earliest_possible_action_date,
  reportedByUser: dto.reported_by_user,
  completedAt: dto.completed_at,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseDsarCaseAssetStatusLogForFE = (dto: DsarCaseAssetStatusLogDTO): DsarCaseAssetStatusLog => ({
  id: dto.id,
  status: parseDsarDomainValueForFE(dto.status),
  loggedAt: dto.logged_at,
  loggedByUser: dto.logged_by_user,
  loggedByUserName: dto.logged_by_user_name,
  comment: dto.comment,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseDsarCaseAssetForFE = (dto: DsarCaseAssetDTO): DsarCaseAsset => ({
  id: dto.id,
  dsarCase: parseDsarCaseAssetCaseSummaryForFE(dto.dsar_case),
  asset: parseDsarCaseAssetSummaryForFE(dto.asset),
  status: parseDsarDomainValueForFE(dto.status),
  dataFound: parseDsarDomainValueForFE(dto.data_found),
  resultSummary: dto.result_summary,
  assetOwnerComment: dto.asset_owner_comment,
  thirdPartyDataRisk: dto.third_party_data_risk,
  manualReviewRequired: dto.manual_review_required,
  dueDate: dto.due_date,
  reportedByUser: dto.reported_by_user,
  completedAt: dto.completed_at,
  requestTypeResults: dto.request_type_results.map(parseDsarCaseAssetRequestTypeResultForFE),
  statusLogs: (dto.status_logs || []).map(parseDsarCaseAssetStatusLogForFE),
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseDsarCaseAssetsForFE = (dtos: DsarCaseAssetDTO[]): DsarCaseAsset[] => dtos.map(parseDsarCaseAssetForFE);
